import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Gift, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../cart/CartContext";

const ComboCard = ({ product, index }) => {
  const { add } = useCart();
  const navigate = useNavigate();
  const size = product.sizes?.find((s) => s.price) || { label: product.unit, price: product.price };

  const addToCart = (goToCart) => {
    add({ slug: product.slug, name: product.name, image: product.cart_image || product.image, size: size.label, price: size.price });
    toast.success(`${product.name} added to cart`);
    if (goToCart) navigate("/cart");
  };

  return (
    <motion.article
      data-testid={`combo-card-${product.slug}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className="group rounded-3xl border border-gold/25 bg-white/[0.04] overflow-hidden"
    >
      <div className="overflow-hidden">
        <img
          src={product.image}
          alt={`${product.name} — PhudG festive gift box`}
          loading="lazy"
          className="w-full aspect-[2/3] object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-7 md:p-8">
        <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">{product.tagline}</p>
        <h3 className="font-serif text-2xl md:text-3xl text-cream mb-3">{product.name}</h3>
        <p className="text-sm font-light text-cream/60 leading-relaxed mb-6">{product.description}</p>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <p className="font-serif text-3xl text-gold">
            ₹{size.price}
            <span className="block text-[10px] tracking-[0.2em] uppercase text-cream/40 font-sans mt-1">Free delivery · Gift-ready</span>
          </p>
          <div className="flex gap-2">
            <motion.button
              data-testid={`combo-add-${product.slug}`}
              onClick={() => addToCart(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-2 rounded-full border border-cream/25 px-5 py-3 text-xs tracking-widest uppercase text-cream/80 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ShoppingCart size={14} /> Add
            </motion.button>
            <motion.button
              data-testid={`combo-gift-${product.slug}`}
              onClick={() => addToCart(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-xs tracking-widest uppercase font-medium text-ink hover:bg-cream transition-colors duration-300"
            >
              <Gift size={14} /> Gift This
            </motion.button>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export const FestiveCombos = ({ products }) => {
  const combos = (products || []).filter((p) => p.combo && p.status === "available");
  if (!combos.length) return null;

  return (
    <section id="gift-combos" data-testid="festive-combos-section" className="relative bg-ink grain py-28 md:py-36">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="md:flex items-end justify-between gap-10 mb-14">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-gold mb-4">Festive Gifting</p>
            <h2 className="font-serif text-5xl md:text-6xl text-cream tracking-tighter">
              Gift the <span className="italic text-gold">good oils.</span>
            </h2>
          </div>
          <p className="text-sm font-light text-cream/50 max-w-sm leading-relaxed mt-6 md:mt-0">
            Ready-made combo boxes for festivals and family — purity makes the warmest gift.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl">
          {combos.map((p, i) => (
            <ComboCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
